import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Svg,
  Polygon,
} from "@react-pdf/renderer";
import { ProgressReportPrefill } from "../../../../hooks/useConstructionProgressReport";
import { formatDate } from "utils/Common/Date";
interface CompanyInfo {
  name?: string;
  legalName?: string;
  address?: string;
  phone?: string;
  email?: string;
}

const DEFAULT_COMPANY_INFO: Required<CompanyInfo> = {
  name: "CSC New England",
  legalName: "Consultant Services New England, LLC",
  address: "23 Lookout Drive, Freeport, ME 04032",
  phone: "(207) 650-7435",
  email: "info@cscnewengland.com",
};

// Mirrors Step7InvoiceRecommendation.tsx's option labels.
const RECOMMENDATION_LABELS: Record<string, string> = {
  approve: "Pay the Draw",
  hold: "Pay with Hold",
  reject: "Do Not Pay",
};

export interface ConstructionReportPdfProps {
  prefill?: ProgressReportPrefill;
  invoiceAmount?: string;
  invoiceDate?: string;
  invoiceNumber?: string;
  reportDate?: string;
  recommendationText?: string;
  // Short cover-page verdict (e.g. "Approve", "Approve with 1 Flag"),
  // distinct from recommendationText's longer free-text notes.
  recommendation?: string;
  overallCscEstimation?: string | number;
  companyInfo?: CompanyInfo;
  signatureDate?: string;
  servicesRequested?: string;
  drawReviewBullets?: string;
  phaseNotes?: Record<string, string>;
  flagNarratives?: Record<string, string>;
  extraFlags?: Array<{
    id: string;
    phase?: string;
    itemLabel: string;
    itemName?: string;
    narrative: string;
  }>;
  scheduleNotes?: string;
  changeOrders?: string;
  bankItems?: string;
  attachmentsList?: string;
  payableTo?: string;
  mailTo?: string;
  deliveryMethod?: string;
  deliveryCc?: string[];
  lockAfterPublish?: string;
  sigName?: string;
  sigAddress?: string;
}

const S = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 64,
    paddingHorizontal: 36,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#111111",
    backgroundColor: "#ffffff",
  },
  headerSmall: { fontSize: 7.5, color: "#909399", marginBottom: 4 },
  headerTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 14,
    color: "#14508c",
    marginBottom: 4,
  },
  headerMeta: { fontSize: 8, color: "#555c66", marginBottom: 12 },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#dde1e6",
    marginBottom: 14,
  },
  section: { marginBottom: 14 },
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#14508c",
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#dde1e6",
  },
  body: { fontSize: 8.5, color: "#111111", lineHeight: 1.5 },
  bodyMuted: { fontSize: 8.5, color: "#909399" },
  row: { flexDirection: "row", marginBottom: 3 },
  label: { fontSize: 8.5, width: 100 },
  value: { fontSize: 8.5, flex: 1 },
  bullet: { flexDirection: "row", marginBottom: 3 },
  bulletDot: { fontSize: 8.5, width: 12, color: "#555c66" },
  bulletText: { fontSize: 8.5, flex: 1, lineHeight: 1.4 },
  bulletFlag: { flexDirection: "row", marginBottom: 3 },
  bulletFlagText: { fontSize: 8.5, flex: 1, lineHeight: 1.4, color: "#c0392b" },
  calloutBox: {
    backgroundColor: "#fff8f0",
    borderLeftWidth: 3,
    borderLeftColor: "#e67e22",
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 14,
  },
  calloutTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#e67e22",
    marginBottom: 4,
  },
  sigRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
  },
  sigName: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    color: "#14508c",
  },
  italic: {
    fontFamily: "Helvetica-BoldOblique",
    color: "#555c66",
  },
  sigAddress: { fontSize: 8, color: "#555c66", marginTop: 2 },
  sigRight: { alignItems: "flex-end" },
  footer: {
    position: "absolute",
    bottom: 16,
    left: 36,
    right: 36,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#dde1e6",
    paddingTop: 5,
  },
  footerText: { fontSize: 7, color: "#909399" },
  inlineStrong: { fontFamily: "Helvetica-Bold" },

  // ── Cover page (Construction Progress Report) ──────────────────────
  coverPage: {
    fontFamily: "Helvetica",
    backgroundColor: "#fdfdfb",
    color: "#111111",
  },
  coverHeaderBand: {
    height: 306, // 4.25in
    backgroundColor: "#0d3a66",
    paddingTop: 43,
    paddingHorizontal: 54,
    paddingBottom: 20,
    position: "relative",
  },
  coverHeaderAccent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: "#e8c471",
  },
  coverLogoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 26,
  },
  coverLogoMark: { flexDirection: "row", alignItems: "center", gap: 10 },
  coverPeaksRow: { flexDirection: "row", gap: 4 },
  coverLogoText: {},
  coverLogoCsc: {
    fontFamily: "Times-Roman",
    fontSize: 22,
    color: "#ffffff",
    letterSpacing: 1,
  },
  coverLogoNe: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    color: "#ffffff",
    letterSpacing: 2,
    marginTop: 2,
  },
  coverLogoTag: {
    fontSize: 7,
    color: "#a8c8ea",
    fontFamily: "Helvetica-Oblique",
    marginTop: 2,
  },
  coverBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#e8c47180",
    borderRadius: 12,
    paddingVertical: 5,
    paddingHorizontal: 12,
  },
  coverBadgeDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#e8c471",
  },
  coverBadgeText: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    color: "#e8c471",
    letterSpacing: 1.2,
  },
  coverTitleEyebrowRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  coverTitleEyebrowLine: {
    width: 26,
    height: 1.5,
    backgroundColor: "#e8c471",
  },
  coverTitleEyebrow: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#e8c471",
    letterSpacing: 1.6,
  },
  coverReportTitle: {
    fontFamily: "Times-Bold",
    fontSize: 30,
    color: "#ffffff",
    lineHeight: 1.15,
    marginBottom: 8,
  },
  coverReportSub: {
    fontFamily: "Helvetica-Oblique",
    fontSize: 9.5,
    color: "#dcc78e",
    lineHeight: 1.5,
    maxWidth: 380,
  },
  coverDrawBadge: {
    position: "absolute",
    top: 73,
    right: 54,
    alignItems: "center",
    backgroundColor: "#c8983a",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    minWidth: 76,
  },
  coverDrawBadgeLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7,
    color: "#ffffffd9",
    letterSpacing: 1.4,
  },
  coverDrawBadgeNumber: {
    fontFamily: "Times-Roman",
    fontSize: 26,
    color: "#ffffff",
    marginTop: 2,
  },
  coverDrawBadgeOf: {
    fontFamily: "Helvetica",
    fontSize: 7,
    color: "#ffffffbf",
    marginTop: 2,
  },
  coverContent: {
    paddingTop: 26,
    paddingHorizontal: 54,
    paddingBottom: 20,
    flexGrow: 1,
  },
  coverProjectEyebrow: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8.5,
    color: "#14508c",
    letterSpacing: 1.4,
    marginBottom: 6,
  },
  coverProjectName: {
    fontFamily: "Times-Roman",
    fontSize: 22,
    color: "#111111",
    lineHeight: 1.15,
    marginBottom: 5,
  },
  coverProjectAddress: { fontSize: 10, color: "#555c66" },
  coverProjectDivider: {
    height: 2,
    backgroundColor: "#14508c",
    width: "50%",
    marginTop: 12,
    marginBottom: 20,
  },
  coverSummaryStrip: {
    flexDirection: "row",
    backgroundColor: "#eaf2fb",
    borderWidth: 1,
    borderColor: "#14508c",
    borderRadius: 6,
    paddingVertical: 12,
    marginBottom: 20,
  },
  coverSsItem: { flex: 1, alignItems: "center" },
  coverSsDivider: { width: 1, backgroundColor: "#14508c80" },
  coverSsLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    color: "#909399",
    letterSpacing: 1,
    marginBottom: 4,
  },
  coverSsValue: { fontFamily: "Times-Bold", fontSize: 15, color: "#14508c" },
  coverSsSub: { fontSize: 7.5, color: "#909399", marginTop: 3 },
  coverDetailsGrid: { marginBottom: 6 },
  coverDetailsRow: { flexDirection: "row", marginBottom: 14 },
  coverDetail: {
    flex: 1,
    borderLeftWidth: 1.5,
    borderLeftColor: "#14508c",
    paddingLeft: 10,
    paddingRight: 10,
  },
  coverDetailLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7,
    color: "#909399",
    letterSpacing: 1,
    marginBottom: 3,
  },
  coverDetailValue: {
    fontFamily: "Times-Roman",
    fontSize: 11,
    color: "#111111",
  },
  coverDetailSub: { fontSize: 7.5, color: "#909399", marginTop: 2 },
  coverFooter: {
    backgroundColor: "#0d3a66",
    paddingVertical: 14,
    paddingHorizontal: 54,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  coverFooterAccent: { height: 3, backgroundColor: "#c8983a" },
  coverFooterLeft: {
    fontSize: 7.5,
    color: "#ffffffe6",
    lineHeight: 1.5,
    maxWidth: 340,
  },
  coverFooterRight: {
    fontSize: 7,
    color: "#a8c8ea",
    letterSpacing: 0.6,
    lineHeight: 1.5,
    textAlign: "right",
  },
});

const fmt = (amount?: number, sym = "$"): string =>
  amount !== undefined
    ? `${sym}${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
    : "—";

// Mirrors the cover reference's three-peak logo mark.
const LogoPeaks = () => (
  <View style={S.coverPeaksRow}>
    {[0, 1, 2].map((i) => (
      <Svg key={i} width={20} height={24}>
        <Polygon points="0,24 10,0 20,24" fill="#ffffff" />
      </Svg>
    ))}
  </View>
);

const BulletList = ({ text }: { text: string }) => {
  const lines = text
    .split("\n")
    .map((l) => l.replace(/^•\s*/, "").trim())
    .filter(Boolean);
  return (
    <View>
      {lines.map((line, i) => (
        <View key={i} style={S.bullet}>
          <Text style={S.bulletDot}>•</Text>
          <Text style={S.bulletText}>{line}</Text>
        </View>
      ))}
    </View>
  );
};

export default function ConstructionReportPdf({
  prefill,
  invoiceAmount = "",
  invoiceDate = "",
  invoiceNumber = "",
  reportDate = "",
  recommendationText = "",
  recommendation = "",
  overallCscEstimation = "",
  companyInfo = DEFAULT_COMPANY_INFO,
  signatureDate = "",
  servicesRequested = "",
  drawReviewBullets = "",
  phaseNotes = {},
  flagNarratives = {},
  extraFlags = [],
  scheduleNotes = "",
  changeOrders = "",
  bankItems = "",
  attachmentsList = "",
  payableTo = "",
  mailTo = "",
  deliveryMethod = "",
  deliveryCc = [],
  lockAfterPublish = "",
  sigName = "",
  sigAddress = "",
}: ConstructionReportPdfProps) {
  const project = prefill?.project;
  const isCommercial = project?.type === "commercial";
  const stakeholders = prefill?.stakeholders;
  const inspection = prefill?.inspection;
  const draw = prefill?.draw;

  const lenderName = stakeholders?.lender?.companyName ?? "";
  const lendingOfficer = stakeholders?.lendingOfficer
    ? `${stakeholders.lendingOfficer?.firstName ?? ""} ${stakeholders.lendingOfficer?.lastName ?? ""}`.trim()
    : "";
  const ownerName = stakeholders?.owner?.companyName ?? "";
  const gcName = stakeholders?.gc
    ? stakeholders.gc.companyName ||
      `${stakeholders.gc?.firstName ?? ""} ${stakeholders.gc.lastName ?? ""}`.trim()
    : "";
  const reportNum = prefill?.nextReportNumber ?? "";
  const drawNum = draw?.drawNumber ?? "";
  const g702 = draw?.g702;
  const sym = g702?.originalContractSum?.currency?.symbol ?? "$";
  const drawNumPadded = drawNum ? String(drawNum).padStart(2, "0") : "";

  const formattedInvoiceNum = invoiceNumber
    ? invoiceNumber.startsWith("#")
      ? invoiceNumber
      : `#${invoiceNumber}`
    : "";
  const forLine = servicesRequested || "—";
  const toLine = [lenderName, lendingOfficer].filter(Boolean).join(" · ");

  const deliveryMethodLabel = (() => {
    switch (deliveryMethod) {
      case "email_platform":
        return "Email + In-platform notification";
      case "platform":
        return "In-platform only";
      case "email":
        return "Email only";
      default:
        return deliveryMethod || "—";
    }
  })();
  const lockLabel =
    lockAfterPublish === "true"
      ? "Yes — lock for editing after publish"
      : lockAfterPublish === "false"
        ? "No — allow revisions"
        : "—";
  // Mirrors Step7InvoiceRecommendation.tsx's option labels. Computed here
  // (not by callers) so every caller can just pass the raw recommendation
  // id and can't forget to format it — same pattern as deliveryMethodLabel.
  const recommendationLabel =
    RECOMMENDATION_LABELS[recommendation] || recommendation || "—";

  const headerTitle = [project?.name, project?.address]
    .filter(Boolean)
    .join(" - ");
  const headerMeta = [
    reportNum && `Report ${reportNum}`,
    formatDate(reportDate || invoiceDate || signatureDate, false),
    lenderName,
    lendingOfficer,
  ]
    .filter(Boolean)
    .join(" · ");

  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const snapshot = prefill?.inspectionSnapshot;
  const allItems: Array<{ id: string; itemLabel: string }> = snapshot?.phases
    ? snapshot.phases.flatMap((p) => p.items)
    : (snapshot?.lineItems ?? []).map((li) => ({
        id: li.id,
        itemLabel: li.aiaItem.itemName,
      }));

  const noteItems = allItems.filter((item) => {
    const hasNote = (phaseNotes[item.id] ?? "").replace(/^•\s*/, "").trim();
    const hasFlag = (flagNarratives[item.id] ?? "").trim();
    return hasNote || hasFlag;
  });
  const extraFlagsFilled = extraFlags.filter((f) => f.narrative.trim());
  const hasProgressContent =
    noteItems.length > 0 || extraFlagsFilled.length > 0;

  // ── Cover page derived values ──────────────────────────────────────
  const coverCompany = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  const copyrightYear = new Date().getFullYear();
  const totalToDatePct =
    g702?.totalCompletedAndStored?.amount != null &&
    g702?.contractSumToDate?.amount
      ? `${((g702.totalCompletedAndStored.amount / g702.contractSumToDate.amount) * 100).toFixed(2)}% of contract`
      : null;
  const inspectorName = inspection?.inspectorName || "—";
  const inspectionPeriod = invoiceDate
    ? `Through ${formatDate(invoiceDate, false)}`
    : "—";
  const coverDetails: { label: string; value: string; sub?: string }[] = [
    {
      label: "Prepared For",
      value: lenderName || "—",
      sub: lendingOfficer || undefined,
    },
    {
      label: "Field Inspection By",
      value: inspectorName,
      sub: inspection?.inspectionDate
        ? `Inspection conducted ${formatDate(inspection.inspectionDate, false)}`
        : undefined,
    },
    {
      label: "Report Reference",
      value: reportNum ? `Report ${reportNum}` : "—",
      sub: reportDate ? `Issued ${formatDate(reportDate, false)}` : undefined,
    },
    { label: "Inspection Period", value: inspectionPeriod },
    { label: "General Contractor", value: gcName || "—" },
    { label: "CSC Recommendation", value: recommendationLabel },
  ];
  const coverDetailRows = [
    coverDetails.slice(0, 2),
    coverDetails.slice(2, 4),
    coverDetails.slice(4, 6),
  ];

  return (
    <Document>
      {/* ══ COVER PAGE — US Letter 8.5×11 ══ */}
      <Page size="A4" style={S.coverPage}>
        <View style={S.coverHeaderBand}>
          <View style={S.coverLogoRow}>
            <View style={S.coverLogoMark}>
              <LogoPeaks />
              <View style={S.coverLogoText}>
                <Text style={S.coverLogoCsc}>CSC</Text>
                <Text style={S.coverLogoNe}>NEW ENGLAND</Text>
                <Text style={S.coverLogoTag}>
                  Lender Construction &amp; Project Consulting
                </Text>
              </View>
            </View>
            <View style={S.coverBadge}>
              <View style={S.coverBadgeDot} />
              <Text style={S.coverBadgeText}>CONFIDENTIAL REPORT</Text>
            </View>
          </View>

          <View style={S.coverTitleEyebrowRow}>
            <View style={S.coverTitleEyebrowLine} />
            <Text style={S.coverTitleEyebrow}>
              DRAW AUTHORIZATION &amp; FIELD INSPECTION FINDINGS
            </Text>
          </View>
          <Text style={S.coverReportTitle}>
            {"Construction\nProgress Report"}
          </Text>
          <Text style={S.coverReportSub}>
            Field-verified progress assessment, AIA G702/G703 draw analysis,
            lien waiver status, and CSC funding recommendation for the current
            draw period.
          </Text>

          <View style={S.coverHeaderAccent} />
        </View>
        {/* Draw # badge */}
        <View style={S.coverDrawBadge}>
          <Text style={S.coverDrawBadgeLabel}>DRAW</Text>
          <Text style={S.coverDrawBadgeNumber}>{drawNumPadded || "—"}</Text>
          <Text style={S.coverDrawBadgeOf}>
            {overallCscEstimation
              ? `of ~ ${overallCscEstimation} estimated`
              : "of ~ — estimated"}
          </Text>
        </View>
        <View style={S.coverContent}>
          <View>
            <Text style={S.coverProjectEyebrow}>PROJECT</Text>
            <Text style={S.coverProjectName}>{project?.name || "—"}</Text>
            <Text style={S.coverProjectAddress}>{project?.address || "—"}</Text>
            <View style={S.coverProjectDivider} />
          </View>

          <View style={S.coverSummaryStrip} wrap={false}>
            <View style={S.coverSsItem}>
              <Text style={S.coverSsLabel}>THIS DRAW</Text>
              <Text style={S.coverSsValue}>
                {fmt(g702?.currentPaymentDue?.amount, sym)}
              </Text>
              <Text style={S.coverSsSub}>Requested by GC</Text>
            </View>
            <View style={S.coverSsDivider} />
            <View style={S.coverSsItem}>
              <Text style={S.coverSsLabel}>TOTAL TO DATE</Text>
              <Text style={S.coverSsValue}>
                {fmt(g702?.totalCompletedAndStored?.amount, sym)}
              </Text>
              {totalToDatePct ? (
                <Text style={S.coverSsSub}>{totalToDatePct}</Text>
              ) : null}
            </View>
            <View style={S.coverSsDivider} />
            <View style={S.coverSsItem}>
              <Text style={S.coverSsLabel}>CONTRACT BALANCE</Text>
              <Text style={S.coverSsValue}>
                {fmt(g702?.balanceToFinish?.amount, sym)}
              </Text>
              <Text style={S.coverSsSub}>Incl. retainage</Text>
            </View>
          </View>

          <View style={S.coverDetailsGrid}>
            {coverDetailRows.map((row, i) => (
              <View key={i} style={S.coverDetailsRow} wrap={false}>
                {row.map((detail) => (
                  <View key={detail.label} style={S.coverDetail}>
                    <Text style={S.coverDetailLabel}>
                      {detail.label.toUpperCase()}
                    </Text>
                    <Text style={S.coverDetailValue}>{detail.value}</Text>
                    {detail.sub ? (
                      <Text style={S.coverDetailSub}>{detail.sub}</Text>
                    ) : null}
                  </View>
                ))}
              </View>
            ))}
          </View>

          {recommendationText ? (
            <View style={S.calloutBox} wrap={false}>
              <Text style={S.calloutTitle}>Recommendation Notes</Text>
              <Text style={S.body}>{recommendationText}</Text>
            </View>
          ) : null}
        </View>{" "}
      </Page>

      {/* ══ EXISTING REPORT CONTENT (unchanged) ══ */}
      <Page size="A4" style={S.page}>
        {/* Header */}
        <Text style={S.headerSmall}>Construction Progress Report</Text>
        <Text style={S.headerTitle}>{headerTitle || "—"}</Text>
        <Text style={S.headerMeta}>{headerMeta || "—"}</Text>
        <View style={S.divider} />

        {/* Invoice */}
        <View style={S.section} wrap={false}>
          <Text style={S.sectionTitle}>
            Invoice{formattedInvoiceNum ? ` ${formattedInvoiceNum}` : ""}
          </Text>
          {toLine ? <Text style={S.body}>To: {toLine}</Text> : null}
          {project?.name ? (
            <Text style={S.body}>
              Project: {project.name}
              {project.address ? ` · ${project.address}` : ""}
            </Text>
          ) : null}
          <Text style={S.body}>
            For: {forLine}
            {invoiceAmount ? `  ·  Total Due: $${invoiceAmount}` : ""}
          </Text>
        </View>

        {/* Project Overview */}
        <View style={S.section} wrap={false}>
          <Text style={S.sectionTitle}>Project Overview</Text>
          {(lenderName || lendingOfficer) && (
            <View style={S.row}>
              <Text style={S.label}>Bank / Lender:</Text>
              <Text style={S.value}>
                {[lenderName, lendingOfficer].filter(Boolean).join(" | ")}
              </Text>
            </View>
          )}
          {project?.name && (
            <View style={S.row}>
              <Text style={S.label}>Project:</Text>
              <Text style={S.value}>
                {[project.name, project.address].filter(Boolean).join(" | ")}
              </Text>
            </View>
          )}
          {project?.description && (
            <View style={S.row}>
              <Text style={S.label}>Description:</Text>
              <Text style={S.value}>{project.description}</Text>
            </View>
          )}
          {ownerName && (
            <View style={S.row}>
              <Text style={S.label}>Owner:</Text>
              <Text style={S.value}>{ownerName}</Text>
            </View>
          )}
          {gcName && (
            <View style={S.row}>
              <Text style={S.label}>Contractor:</Text>
              <Text style={S.value}>{gcName}</Text>
            </View>
          )}
          {forLine && (
            <View style={S.row}>
              <Text style={S.label}>Services:</Text>
              <Text style={S.value}>{forLine}</Text>
            </View>
          )}
          {inspection?.inspectionDate && (
            <View style={S.row}>
              <Text style={S.label}>Inspection Date:</Text>
              <Text style={S.value}>
                {inspection.inspectionDate}
                {reportNum ? `  ·  Report ${reportNum}` : ""}
              </Text>
            </View>
          )}
        </View>

        {/* Draw Summary */}
        <View style={S.section}>
          <Text style={S.sectionTitle}>
            Draw Summary
            {drawNumPadded ? ` — Application #${drawNumPadded}` : ""}
          </Text>
          <Text style={S.body}>
            {gcName || "The contractor"} have submitted Application for Payment{" "}
            {drawNumPadded || "—"} Period to{" "}
            {formatDate(invoiceDate, false) || "—"} in the amount of{" "}
            {fmt(g702?.currentPaymentDue?.amount, sym)}.
          </Text>
          <Text style={[S.body, { marginTop: 6 }]}>
            Contract Sum To Date: {fmt(g702?.contractSumToDate?.amount, sym)}
            {"  ·  "}
            Total Completed To Date:{" "}
            {fmt(g702?.totalCompletedAndStored?.amount, sym)}
            {"  ·  "}
            Retainage: {fmt(g702?.totalRetainage?.amount, sym)}
            {"  ·  "}
            Total Earned Less Retainage:{" "}
            {fmt(g702?.totalEarnedLessRetainage?.amount, sym)}
            {"  ·  "}
            Less Previous Certificates:{" "}
            {fmt(g702?.lessPreviousCertificates?.amount, sym)}
            {"  ·  "}
            Current Payment Due: {fmt(g702?.currentPaymentDue?.amount, sym)}
            {"  ·  "}
            Balance to Finish: {fmt(g702?.balanceToFinish?.amount, sym)}
          </Text>
        </View>

        {/* Draw Review */}
        <View style={S.section}>
          <Text style={S.sectionTitle}>Draw Review</Text>
          {drawReviewBullets ? (
            <BulletList text={drawReviewBullets} />
          ) : (
            <Text style={S.bodyMuted}>—</Text>
          )}
        </View>

        {/* Progress Report */}
        <View style={S.section}>
          <Text style={S.sectionTitle}>Progress Report</Text>
          {hasProgressContent ? (
            <View>
              {noteItems.map((item) => {
                const flagNarrative = (flagNarratives[item.id] ?? "").trim();
                const lines = (phaseNotes[item.id] ?? "")
                  .split("\n")
                  .map((l) => l.replace(/^•\s*/, "").trim())
                  .filter(Boolean);
                return (
                  <View key={item.id} style={S.bullet}>
                    <Text style={S.bulletDot}>•</Text>
                    <Text style={S.bulletText}>
                      {item.itemLabel}:{" "}
                      {lines.map((line, i) => (
                        <Text key={i}>
                          {i > 0 ? ". " : ""}
                          {line}
                        </Text>
                      ))}
                      {flagNarrative ? (
                        <Text style={{ color: "#c0392b" }}>
                          {lines.length > 0 ? ". " : ""}
                          {flagNarrative}
                        </Text>
                      ) : null}
                    </Text>
                  </View>
                );
              })}
              {extraFlagsFilled.map((flag) => (
                <View key={flag.id} style={S.bulletFlag}>
                  <Text style={S.bulletDot}>•</Text>
                  <Text style={S.bulletFlagText}>
                    {flag.itemName ?? flag.itemLabel}: {flag.narrative.trim()}
                  </Text>
                </View>
              ))}
            </View>
          ) : (
            <Text style={S.bodyMuted}>—</Text>
          )}
          {!isCommercial ? (
            <View style={[S.calloutBox, { marginTop: 8, marginBottom: 0 }]}>
              <Text style={S.calloutTitle}>Note</Text>
              <Text style={S.body}>
                CSC estimated % complete is based on industry average and not
                the project specific schedule of values.
              </Text>
            </View>
          ) : null}
        </View>

        {/* Schedule */}
        <View style={S.section} wrap={false}>
          <Text style={S.sectionTitle}>Schedule</Text>
          {scheduleNotes ? (
            <BulletList text={scheduleNotes} />
          ) : (
            <Text style={S.bodyMuted}>—</Text>
          )}
        </View>

        {/* Change Orders */}
        <View style={S.section} wrap={false}>
          <Text style={S.sectionTitle}>Change Orders</Text>
          <Text style={changeOrders ? S.body : S.bodyMuted}>
            {changeOrders || "—"}
          </Text>
        </View>

        {/* Items for Bank to Verify */}
        <View style={S.section} wrap={false}>
          <Text style={S.sectionTitle}>Items for Bank to Verify</Text>
          <Text style={bankItems ? S.body : S.bodyMuted}>
            {bankItems || "—"}
          </Text>
        </View>

        {/* Information of Action Required (callout) */}
        <View style={S.calloutBox} wrap={false}>
          <Text style={S.calloutTitle}>Information of Action Required</Text>
          <Text style={S.body}>
            {recommendationText ||
              "Pay the Draw as noted in this report upon Lender approval."}
          </Text>
        </View>

        {/* Attachments */}
        <View style={S.section} wrap={false}>
          <Text style={S.sectionTitle}>Attachments</Text>
          <Text style={attachmentsList ? S.body : S.bodyMuted}>
            {attachmentsList || "—"}
          </Text>
        </View>

        {/* Delivery Settings */}
        <View style={S.section} wrap={false}>
          <Text style={S.sectionTitle}>Delivery Settings</Text>
          <Text style={S.body}>Deliver To: {toLine || "—"}</Text>
          {deliveryCc.length > 0 && (
            <Text style={S.body}>CC: {deliveryCc.join(", ")}</Text>
          )}
          <Text style={S.body}>Make Check Payable To: {payableTo || "—"}</Text>
          <Text style={S.body}>Mail Check To: {mailTo || "—"}</Text>
          <Text style={S.body}>Delivery Method: {deliveryMethodLabel}</Text>
          <Text style={S.body}>Lock After Publish: {lockLabel}</Text>
        </View>

        {/* Signature */}
        <View style={S.sigRow} wrap={false}>
          <View>
            <Text style={S.sigName}>{sigName || "—"}</Text>
            {sigAddress ? <Text style={S.sigAddress}>{sigAddress}</Text> : null}
          </View>
          <View style={S.sigRight}>
            <Text style={[S.sigName, S.italic]}>{sigName}</Text>
            <Text style={S.sigAddress}>
              {formatDate(signatureDate, false) || "—"}
            </Text>
          </View>
        </View>

        {/* Closing company footer — same block as the cover page, shown once
            at the end of the report rather than repeated on every page.
            marginHorizontal cancels out S.page's paddingHorizontal (36) so
            this block reaches the true page edges like it does on the cover. */}
        <View style={{ marginTop: 18, marginHorizontal: -36 }} wrap={false}>
          <View style={S.coverFooterAccent} />
          <View style={S.coverFooter}>
            <Text style={S.coverFooterLeft}>
              <Text style={{ fontFamily: "Helvetica-Bold", color: "#ffffff" }}>
                {coverCompany.name}
              </Text>{" "}
              · {coverCompany.legalName}
              {"\n"}
              {coverCompany.address} · {coverCompany.phone} ·{" "}
              {coverCompany.email}
            </Text>
            <Text style={S.coverFooterRight}>
              {"Confidential\n"}© {copyrightYear} · All rights reserved
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={S.footer} fixed>
          <Text style={S.footerText}>
            {sigName} · Construction Progress Report · {today}
          </Text>
          <Text
            style={S.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
